import React, { useMemo, useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import Switch from "react-switch";
import { useSelector } from "react-redux";
import { hasPermission } from "utils/permissionHelper";
import { PERMISSIONS } from "constants/permissionTypes";
import { MODULES } from "constants/modulesTypes";
import { useNavigate } from "react-router-dom";
import moment from "moment";

//import components
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/DataTableContainer1";
import BatchModal from "../../components/Common/BatchModal";

const DatatableTables = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortingBy, setSortingBy] = useState("");
  const [direction, setDirection] = useState("");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [formData, setFormData] = useState({});
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem("authUser"));

  const Offsymbol = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          fontSize: 12,
          color: "#fff",
          paddingRight: 2
        }}
      >
        {" "}
        No
      </div>
    );
  };

  const OnSymbol = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          fontSize: 12,
          color: "#fff",
          paddingRight: 2
        }}
      >
        {" "}
        Yes
      </div>
    );
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const query = new URLSearchParams({
        page,
        countPerPage,
        sortingBy,
        direction,
        search,
      }).toString();

      const res = await fetch(`https://api.kunalsdrumclass.com/api/GetAllBatches?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows;
      setTableData(
        list.map((item, index) => ({
          ...item,
          index: (page - 1) * countPerPage + index + 1,
        }))
      );
      setTotalRecords(total);
    } catch (error) {
      console.error("Error loading batches:", error);
    } finally {
      setLoading(false);
    }
  }, [page, countPerPage, sortingBy, direction, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openModal = (mode, data = {}) => {
    setModalMode(mode);

    if (mode === "create") {
      setFormData({
        Batch_Day: "",
        Batch_Time: "",
        Name: "",
        Max_Students: "",
        IsActive: true,
      });
    } else {
      setFormData({
        ...data,
        IsActive: Boolean(data.IsActive),
      });
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({});
  };

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setCountPerPage(size);
    setPage(1);
  };

  const handleSortChange = (columnId, desc) => {
    if (!columnId) {
      setSortingBy(null);
      setDirection("Asc");
    } else {
      setSortingBy(columnId);
      setDirection(desc ? "Desc" : "Asc");
    }
    setPage(1);
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const sortingState = useMemo(() => {
    if (!sortingBy) return [];
    return [{ id: sortingBy, desc: direction === "Desc" }];
  }, [sortingBy, direction]);

  const permissionMap = useSelector(
    state => state.permissions?.permissions || {}
  );

  const handleCreate = async () => {
    setLoading(true);
    try {
      let finalData = { ...formData };
      finalData = {
        id: formData.Id,
        name: formData.Name,
        day: formData.Batch_Day,
        time: formData.Batch_Time,
        maxStudents: formData.Max_Students,
        userId: user.Id,
        isActive: formData.IsActive
      };

      const res = await fetch("https://api.kunalsdrumclass.com/api/InsertUpdateBatch", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalData),
      });

      const data = await res.json();
      const status = data?.recordsets?.[0]?.[0]?.Status;

      if (status === 0) {
        await Swal.fire({
          title: "Duplicate Record",
          text: `batch already exists!`,
          icon: "warning",
          customClass: {
            popup: "swal2-material-ui",
            confirmButton: "btn btn-sm rounded-8 btn-warning",
          },
        });
        return;
      }

      await Swal.fire({
        title: "Success!",
        text: `batch created successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      closeModal();
      fetchData();
    } catch (error) {
      console.error("Create Error:", error);
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (
      Number(formData.Max_Students) <
      Number(formData.Occupied_Slots)
    ) {
      await Swal.fire({
        title: "Invalid Capacity",
        text: `Minimum capacity should be ${formData.Occupied_Slots} because students are already assigned.`,
        icon: "warning",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-warning",
        },
      });

      return;
    }

    setLoading(true);
    try {
      let finalData = { ...formData };
      finalData = {
        id: formData.Id,
        name: formData.Name,
        day: formData.Batch_Day,
        time: formData.Batch_Time,
        maxStudents: formData.Max_Students,
        userId: user.Id,
        isActive: formData.IsActive
      };

      const res = await fetch("https://api.kunalsdrumclass.com/api/InsertUpdateBatch", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalData),
      });

      const data = await res.json();
      const status = data?.recordsets?.[0]?.[0]?.Status;

      if (status === 0) {
        await Swal.fire({
          title: "Duplicate Record",
          text: `batch already exists!`,
          icon: "warning",
          customClass: {
            popup: "swal2-material-ui",
            confirmButton: "btn btn-sm rounded-8 btn-warning",
          },
        });
        return;
      }

      await Swal.fire({
        title: "Success!",
        text: `batch updated successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      closeModal();
      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const onModalSubmit = () => {
    if (modalMode === "create") handleCreate();
    if (modalMode === "edit") handleEdit();
  };

  const handleStatusToggle = useCallback(async (rowData, checked) => {
    try {
      setTableData(prev =>
        prev.map(item =>
          item.Id === rowData.Id
            ? { ...item, IsActive: checked }
            : item
        )
      );

      const payload = {
        id: rowData.Id,
        name: rowData.Name,
        day: rowData.Batch_Day,
        time: rowData.Batch_Time,
        maxStudents: rowData.Max_Students,
        userId: user.Id,
        isActive: checked,
      };

      const res = await fetch("https://api.kunalsdrumclass.com/api/InsertUpdateBatch",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();
      const status = data?.recordsets?.[0]?.[0]?.Status;

      if (status !== 1) {
        throw new Error("Failed");
      }

      await Swal.fire({
        title: "Success!",
        text: `batch updated successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    }
  }, [fetchData, token, user.Id]);

  const columns = useMemo(
    () => [
      {
        header: "Id",
        accessorKey: "Id",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Name",
        meta: {
          sticky: true,
        },
        accessorKey: "Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Day",
        accessorKey: "Batch_Day",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Time",
        accessorKey: "Batch_Time",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Capacity",
        accessorKey: "Max_Students",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Available Slots",
        accessorKey: "Available_Slots",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Created Date",
        accessorKey: "CreatedOn",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
      },
      {
        header: "Enabled",
        accessorKey: "IsActive",
        enableSorting: true,
        enableColumnFilter: false,

        cell: ({ row }) => {
          const rowData = row.original;
          const isBatchOccupied = rowData.Max_Students !== rowData.Available_Slots;
          const hasEditPermission = hasPermission(permissionMap, MODULES.BATCHES, PERMISSIONS.EDIT);

          return (
            <div className="d-flex align-items-center gap-2">
              <Switch
                checked={Boolean(rowData.IsActive)}
                onChange={(checked) =>
                  handleStatusToggle(rowData, checked)
                }
                onColor="#626ed4"
                uncheckedIcon={<Offsymbol />}
                checkedIcon={<OnSymbol />}
                disabled={isBatchOccupied || !hasEditPermission}
              />
            </div>
          );
        },
      },
      {
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const isDisabled = rowData.Max_Students !== rowData.Available_Slots;

          return (
            <div className="d-flex gap-2">
              {hasPermission(permissionMap, MODULES.BATCHES, PERMISSIONS.EDIT) && (
                <button className="btn btn-sm btn-secondary" onClick={() => openModal("edit", rowData)}>Edit</button>
              )}
              {hasPermission(permissionMap, MODULES.BATCHES, PERMISSIONS.DELETE) && (
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(rowData)} disabled={isDisabled}>Delete</button>
              )}
              {hasPermission(permissionMap, MODULES.BATCHES, PERMISSIONS.VIEW) && (
                <button className="btn btn-sm btn-primary" onClick={() => navigate(`/batch/view/${rowData.Batch_Day}/${rowData.Batch_Time}`)}>View Students</button>
              )}
            </div>
          );
        },
      },
    ],
    [permissionMap, handleStatusToggle]
  );

  const handleDelete = async (rowData) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to delete this batch?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;
    try {
      const payload = { id: rowData.Id, modifiedBy: user.Id };
      await fetch(`https://api.kunalsdrumclass.com/api/DeleteBatchById?id=${payload.id}&modifiedBy=${payload.modifiedBy}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      await Swal.fire({
        title: "Success!",
        text: `batch deleted successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });
      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    }
  };

  //meta title
  document.title = "Batches | KDC - Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Batches"
          breadcrumbItems={[
            { title: "Batches" }
          ]}
        />

        {hasPermission(permissionMap, MODULES.BATCHES, PERMISSIONS.INSERT) && (
          <div className="d-flex justify-content-end mb-3">
            <button className="btn btn-primary mb-3" onClick={() => openModal("create")}>
              + Create Batch
            </button>
          </div>
        )}

        <TableContainer
          columns={columns}
          data={tableData}
          page={page}
          countPerPage={countPerPage}
          totalRecords={totalRecords}
          sortingState={sortingState}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          searchValue={search}
          onSearch={handleSearch}
          isLoading={loading}
          isPagination
          isGlobalFilter
          isCustomPageSize
          SearchPlaceholder="search..."
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <BatchModal
          loading={loading}
          show={showModal}
          mode={modalMode}
          formData={formData}
          setFormData={setFormData}
          onSubmit={onModalSubmit}
          onClose={closeModal}
        />
      </div>
    </div>
  );
};

export default DatatableTables;
